import React, { useState, useEffect, useMemo } from 'react'
import './AlertHub.css'
import { useAuth } from '../../Auth';
import PortalHeader from '../../client-dashboard/clientNavbar/PortalHeader'


// Icons
import { MdRadar, MdOutlineError } from "react-icons/md";
import { FaGlobeAmericas, FaServer, FaChartLine, FaUserCircle } from "react-icons/fa";
import { FaMicrochip } from "react-icons/fa6";
import { LuBrain } from "react-icons/lu";
import { IoInformationCircle, IoBuild } from "react-icons/io5";
import { TiWarning } from "react-icons/ti";
import { BiSolidArchive } from "react-icons/bi";

interface Alerts {
    id: number;
    category: string;
    title: string;
    body: string;
    type: string;
    created_at: any;
    api_key_name: string;
    owner: number;
    archive: boolean;
}

interface Department {
    user_id: number;
    department: string;
}

const baseDepartments = [
    { name: "Detection", icon: <MdRadar className="alert-icon" /> },
    { name: "GIS", icon: <FaGlobeAmericas className="alert-icon" /> },
    { name: "Hardware", icon: <FaMicrochip className="alert-icon" /> },
    { name: "Manufacture", icon: <IoBuild className="alert-icon" /> },
    { name: "Server", icon: <FaServer className="alert-icon" /> },
    { name: "AI", icon: <LuBrain className="alert-icon" /> },
    { name: "Marketing", icon: <FaChartLine className="alert-icon" /> },
    { name: "Account", icon: <FaUserCircle className="alert-icon" /> },
];

const getBackgroundColor = (dept: string) => {
    switch (dept) {
        case "Detection": return "#1E88E5";
        case "GIS": return "#43A047";
        case "Hardware": return "#FB8C00";
        case "Manufacture": return "#969ba9";
        case "Server": return "#8E24AA";
        case "AI": return "#3949AB";
        case "Marketing": return "#E53935";
        case "Account": return "#00897B";
        default: return "grey";
    }
}

const getAlertTypeClass = (type: string) => {
    switch (type) {
        case "info": return "alert-info";
        case "warning": return "alert-warning";
        case "error": return "alert-error";
        default: return "";
    }
}

const AlertHub = () => {
    const [allAlerts, setAllAlerts] = useState<Alerts[]>([]);
    const [departmentsPerUser, setDepartmentsPerUser] = useState<Department[]>([]);
    const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
    const [showArchived, setShowArchived] = useState(false);

    // 🔎 Filter states
    const [searchTerm, setSearchTerm] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [alertType, setAlertType] = useState("");

    const { department: userDepartment } = useAuth();

    // ✅ Reorder departments so userDepartment is first
    const departments = useMemo(() => {
        if (!userDepartment) return baseDepartments;
        const userDeptObj = baseDepartments.find(d => d.name === userDepartment);
        if (!userDeptObj) return baseDepartments;
        return [userDeptObj, ...baseDepartments.filter(d => d.name !== userDepartment)];
    }, [userDepartment]);

    // ✅ update default selection
    // Set default department once when userDepartment changes (but not on manual click)
    useEffect(() => {
        if (userDepartment) {
            setSelectedDepartment(userDepartment);
        }
    }, [userDepartment]);


    // Fetch alerts every 10s
    useEffect(() => {
        const fetchAlerts = async () => {
            try {
                const res = await fetch('/api/alerts/', { credentials: 'same-origin' });
                if (!res.ok) return;

                const data = await res.json();

                setAllAlerts(prev => {
                    // Prevent unnecessary re-render if data is the same
                    const prevStr = JSON.stringify(prev);
                    const newStr = JSON.stringify(data);
                    if (prevStr === newStr) return prev;
                    return data;
                });
            } catch (err) {
                console.error("Error fetching alerts:", err);
            }
        };


        // initial fetch
        fetchAlerts();

        // set interval
        const intervalId = setInterval(fetchAlerts, 10000); // 10s

        // cleanup on unmount
        return () => clearInterval(intervalId);
    }, []);

    // Fetch departments
    useEffect(() => {
        const fetchDepartments = async () => {
            const res = await fetch('/portal/api/department_view/', { credentials: 'same-origin' });
            const data = await res.json();
            if (res.ok) setDepartmentsPerUser(data);
        };
        fetchDepartments();
    }, []);

    // Map user_id -> department
    const userIdToDepartment: Record<number, string> = {};
    departmentsPerUser.forEach(d => {
        if (d.department) userIdToDepartment[d.user_id] = d.department;
    });

    const getDepartmentForAlert = (ownerId: number) => userIdToDepartment[ownerId] || "Unknown";

    // Count errors (only active)
    const errorCounts: Record<string, number> = {};
    allAlerts.forEach(alert => {
        if (!alert.archive && alert.type?.toLowerCase() === "error") {
            const dept = getDepartmentForAlert(alert.owner);
            errorCounts[dept] = (errorCounts[dept] || 0) + 1;
        }
    });

    // Final filter logic
    const filteredAlerts = allAlerts.filter(alert => {
        const deptMatch = selectedDepartment ? getDepartmentForAlert(alert.owner) === selectedDepartment : true;
        const archiveMatch = showArchived ? alert.archive : !alert.archive;

        const searchMatch = searchTerm
            ? (alert.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                alert.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                alert.body?.toLowerCase().includes(searchTerm.toLowerCase()))
            : true;

        const alertDate = new Date(alert.created_at);

        const fromDateMatch = fromDate
            ? alertDate >= new Date(`${fromDate}T00:00:00`)
            : true;

        const toDateMatch = toDate
            ? alertDate <= new Date(`${toDate}T23:59:59`)
            : true;

        const typeMatch = alertType ? alert.type?.toLowerCase() === alertType.toLowerCase() : true;

        return deptMatch && archiveMatch && searchMatch && fromDateMatch && toDateMatch && typeMatch;
    });

    function DateInput({ label, value, onChange }: { label: string, value: string, onChange: (v: string) => void }) {
        const [focus, setFocus] = useState(false);

        return (
            <input
                type={focus || value ? "date" : "text"}
                value={value}
                onFocus={() => setFocus(true)}
                onBlur={() => setFocus(false)}
                onChange={(e) => onChange(e.target.value)}
                placeholder={label}
                className="date-input"
            />
        );
    }

    // 🔄 Carousel logic for alert-carousel
    const [carouselIndex, setCarouselIndex] = useState(0);

    const calculateVisibleCount = (width: number) => {
        // 🔧 clamp the first breakpoint to avoid Chrome off-by-1 issues
        if (width <= 480) return 1;
        if (width <= 700) return 2;
        if (width <= 900) return 3;
        if (width <= 1100) return 4;
        if (width <= 1300) return 5;
        if (width <= 1500) return 6;
        if (width <= 1650) return 7;
        return departments.length;
    };

    const [visibleCount, setVisibleCount] = useState(calculateVisibleCount(window.innerWidth));
    const [isMobileCarousel, setIsMobileCarousel] = useState(window.innerWidth <= 1650);

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            let count = departments.length;
            let mobile = false;

            if (width <= 480) { count = 1; mobile = true; }
            else if (width <= 700) { count = 2; mobile = true; }
            else if (width <= 900) { count = 3; mobile = true; }
            else if (width <= 1100) { count = 4; mobile = true; }
            else if (width <= 1300) { count = 5; mobile = true; }
            else if (width <= 1500) { count = 6; mobile = true; }
            else if (width <= 1650) { count = 7; mobile = true; }

            setVisibleCount(count);
            setIsMobileCarousel(mobile);
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [departments.length]);


    const handleNext = () => {
        if (isMobileCarousel) {
            setCarouselIndex((prev) => (prev + 1) % departments.length);
        }
    };

    const handlePrev = () => {
        if (isMobileCarousel) {
            setCarouselIndex((prev) => (prev - 1 + departments.length) % departments.length);
        }
    };

    const getVisibleDepartments = () => {
        if (isMobileCarousel) {
            const visible: typeof departments = [];
            for (let i = 0; i < visibleCount; i++) {
                visible.push(departments[(carouselIndex + i) % departments.length]);
            }
            return visible;
        }
        return departments;
    };

    useEffect(() => {
        let resizeTimer: NodeJS.Timeout;

        const updateCounts = () => {
            const width = window.innerWidth;
            const count = calculateVisibleCount(width);
            setVisibleCount(count);
            setIsMobileCarousel(count < departments.length);
        };

        const handleResize = () => {
            if (resizeTimer) clearTimeout(resizeTimer);
            resizeTimer = setTimeout(updateCounts, 150); // debounce
        };

        updateCounts(); // run once at mount

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
            if (resizeTimer) clearTimeout(resizeTimer);
        };
    }, [departments.length]);

    // Allowing to archive ERROR alerts

    const fetchCsrfToken = async () => {
        const response = await fetch('/api/csrf-token/', {
            method: 'GET',
            credentials: 'same-origin', // include cookies in the request
        });

        if (response.ok) {
            const data = await response.json();
            return data.csrfToken; // Return the CSRF token
        } else {
            console.error('Failed to fetch CSRF token');
            return null;
        }
    };


    const toggleArchiveAlert = async (alertId: number, currentlyArchived: boolean) => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            const res = await fetch(`/api/alerts/${alertId}/archive`, {
                method: "PUT",
                credentials: "same-origin",
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken
                },
                body: JSON.stringify({ archive: !currentlyArchived })
            });

            if (!res.ok) throw new Error("Failed to update alert");

            // Optimistically update local state
            setAllAlerts(prev =>
                prev.map(a => (a.id === alertId ? { ...a, archive: !currentlyArchived } : a))
            );
        } catch (err) {
            console.error(err);
        }
    };





    return (
        <div className='notification-hub-wrapper'>

            <PortalHeader />

            <div className='alert-side-menu-and-body'>
                <div className='alert-side-menu'>


                </div>
                <div className='alert-body'>

                    <h1>Alerts</h1>

                    {/* Active/Archived Toggle */}
                    <div className='active-archived-filter'>
                        <button
                            className={showArchived ? "" : "isActive"}
                            onClick={() => setShowArchived(false)}
                        >
                            Active Alerts
                        </button>
                        <button
                            className={showArchived ? "isActive" : ""}
                            onClick={() => setShowArchived(true)}
                        >
                            Archived Alerts
                        </button>
                    </div>

                    {/* Top carousel */}
                    <div className="alert-carousel-track">

                        {isMobileCarousel && (
                            <button className="alert-carousel-arrow alert-arrow-left" onClick={handlePrev}>
                                &lt;
                            </button>
                        )}

                        {getVisibleDepartments().map((dept, idx) => {
                            const count = errorCounts[dept.name] || 0;
                            return (
                                <div
                                    key={idx}
                                    className="alert-card-wrapper"
                                    onClick={() => {
                                        if (selectedDepartment === dept.name) {
                                            setSelectedDepartment(null); // unselect if clicking the same card
                                        } else {
                                            setSelectedDepartment(dept.name); // select new department
                                        }
                                    }}

                                >
                                    <div
                                        className="alert-card"
                                        style={{ backgroundColor: getBackgroundColor(dept.name) }}
                                    >
                                        <div className="alert-card-text">
                                            <h2>{dept.name}</h2>
                                            <p>{count} Error Alert{count !== 1 ? "s" : ""}</p>
                                        </div>
                                        {dept.icon}
                                    </div>
                                    {selectedDepartment && selectedDepartment !== dept.name && (
                                        <div className="alert-card-overlay"></div>
                                    )}
                                </div>
                            );
                        })}

                        {isMobileCarousel && (
                            <button className="alert-carousel-arrow alert-arrow-right" onClick={handleNext}>
                                &gt;
                            </button>
                        )}

                    </div>

                    {/* Filters */}
                    <div className='alert-filters'>
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search alert"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />

                        {/* From Date */}
                        <DateInput
                            label="From"
                            value={fromDate}
                            onChange={(val) => setFromDate(val)}
                        />

                        {/* To Date */}
                        <DateInput
                            label="To"
                            value={toDate}
                            onChange={(val) => setToDate(val)}
                        />

                        <select
                            className="type-dropdown"
                            value={alertType}
                            onChange={(e) => setAlertType(e.target.value)}
                        >
                            <option value="">All Types</option>
                            <option value="info">Info</option>
                            <option value="warning">Warning</option>
                            <option value="error">Error</option>
                        </select>
                    </div>

                    <div className='alert-filters2'>
                        {/* From Date */}
                        <DateInput
                            label="From"
                            value={fromDate}
                            onChange={(val) => setFromDate(val)}
                        />

                        {/* To Date */}
                        <DateInput
                            label="To"
                            value={toDate}
                            onChange={(val) => setToDate(val)}
                        />

                    </div>

                    {/* Alert list */}
                    <div className='alert-list'>
                        {filteredAlerts.map(alert => {
                            const deptName = getDepartmentForAlert(alert.owner);
                            return (
                                <div key={alert.id} className='alert-widget'>
                                    <div className='alert-type-and-title'>
                                        <div className={`alert-type-icon ${getAlertTypeClass(alert.type?.toLowerCase())}`}>
                                            {alert.type?.toLowerCase() === "info" && <IoInformationCircle className="alert-icon2" />}
                                            {alert.type?.toLowerCase() === "warning" && <TiWarning className="alert-icon2" />}
                                            {alert.type?.toLowerCase() === "error" && <MdOutlineError className="alert-icon2" />}
                                        </div>
                                        <div className='alert-title-and-date'>
                                            <div className='alert-title'>
                                                <h2>{alert.title}</h2>
                                                <p>Category: {alert.category}</p>
                                            </div>
                                            {alert.type?.toLowerCase() === "error" ? (
                                                <div className='alert-date'>
                                                    <span>{new Date(alert.created_at).toLocaleDateString()}</span>
                                                    <span className="alert-time">
                                                        {new Date(alert.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </span>
                                                    <button
                                                        className='archive-button'
                                                        onClick={() => toggleArchiveAlert(alert.id, alert.archive)}
                                                    >
                                                        <BiSolidArchive /> {alert.archive ? "Unarchive" : "Archive"}
                                                    </button>


                                                </div>
                                            ) : (
                                                <div className="alert-date">
                                                    <span>{new Date(alert.created_at).toLocaleDateString()}</span>
                                                    <span className="alert-time">
                                                        {new Date(alert.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </span>
                                                </div>

                                            )}
                                        </div>
                                    </div>
                                    <p>{alert.body}</p>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
        </div>
    )
}


export default AlertHub;

